import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { motion } from "framer-motion";
export default function PlanCard({ plan, index, isActive = false }) {
  const theme = useTheme();
  const hasDiscount = plan.discountPrice && plan.discountPrice < plan.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      viewport={{ once: true }}
      style={{ height: "100%" }}
    >
      <Card
        onClick={() => window.open("https://t.me/adinzoneadmin")}
        sx={{
          height: "100%",
          minHeight: 520,
          position: "relative",
          borderRadius: 6,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          // Glass background
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: hasDiscount
            ? "2px solid rgba(255, 255, 255, 0.3)"
            : "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: `
            0 8px 32px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `,
          // Background image behind glass
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${import.meta.env.VITE_URL + plan?.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.3) saturate(0.5)",
            zIndex: -1,
          },
          "&:hover": {
            transform: "translateY(-2px) scale(1.01    )",
            boxShadow: `
              0 20px 60px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.2)
            `,
            border: hasDiscount
              ? "2px solid rgba(255, 255, 255, 0.5)"
              : "1px solid rgba(255, 255, 255, 0.3)",
            "& .glass-highlight": {
              opacity: 1,
              transform: "translateX(100%)",
            },
          },
        }}
      >
        {/* Glass highlight effect */}
        <Box
          className="glass-highlight"
          sx={{
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)",
            transition: "all 0.6s ease",
            opacity: 0,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />

        <CardContent
          sx={{
            position: "relative",
            zIndex: 2,
            p: 4,
            height: "100%",
            minHeight: 520,
            display: "flex",
            flexDirection: "column",
            color: "#fff",
          }}
        >
          {/* Badge Section */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            {hasDiscount ? (
              <Chip
                label={`${plan.discountPercent}% تخفیف`}
                icon={
                  <StarIcon
                    sx={{
                      fontSize: 16,
                      color: `${theme.palette.secondary.main} !important`,
                    }}
                  />
                }
                sx={{
                  bgcolor: "rgba(0, 55, 255, 0.15)",
                  color: `${theme.palette.secondary.main} !important`,
                  fontWeight: "bold",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(30, 0, 255, 0.3)",
                  "& .MuiChip-icon": {
                    color: `${theme.palette.secondary.main} !important`,
                  },
                }}
                size="small"
              />
            ) : (
              <Box />
            )}
            {plan.isPopular && (
              <Chip
                label="محبوب"
                icon={<WorkspacePremiumIcon sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  color: "#fff",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
                size="small"
              />
            )}
          </Box>

          {/* Plan Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              textAlign: "center",
              mb: 1,
              textShadow: "0 2px 10px rgba(0,0,0,0.3)",
              fontSize: { xs: "1.3rem", md: "1.5rem" },
            }}
          >
            {plan.name || plan.duration}
          </Typography>

          {/* Duration */}
          {plan.duration && (
            <Typography
              variant="body2"
              textAlign="center"
              sx={{
                color: "rgba(255,255,255,0.7)",
                mb: 3,
                pb: 3,
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              ⏱ {plan.duration?.toLocaleString("fa-IR")} ماهه
            </Typography>
          )}

          {/* Price Section */}
          <Box
            sx={{
              textAlign: "center",
              mb: 4,
              background: "rgba(255,255,255,0.05)",
              borderRadius: 3,
              py: 2,
              px: 2,
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            {hasDiscount ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "line-through",
                    mb: 0.5,
                  }}
                >
                  {plan.price.toLocaleString()} تومان
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    {plan.discountPrice.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    تومان
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {plan.price.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                >
                  تومان
                </Typography>
              </Box>
            )}
          </Box>

          {/* Features List */}
          <List
            dense
            sx={{
              flex: 1,
              //   minHeight: 220,
              //   maxHeight: 220,
              overflow: "hidden",
              direction: "rtl",
              mb: 3,
              "& *": {
                direction: "rtl !important",
              },
            }}
          >
            {plan.descriptions && plan.descriptions.length > 0 ? (
              plan.descriptions.slice(0, 5).map((desc, i) => (
                <ListItem key={desc._id || i} sx={{ px: 0, py: 0.7 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <CheckCircleIcon
                      sx={{
                        fontSize: 18,
                        color: hasDiscount
                          ? theme.palette.secondary.main
                          : theme.palette.secondary.dark,
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                      }}
                    />
                  </ListItemIcon>
                  <Box>
                    {desc.title && (
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          color: "#fff",
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 1,
                          overflow: "hidden",
                          direction: "rtl",
                          textAlign: "right",
                        }}
                      >
                        {desc.title}
                      </Typography>
                    )}
                    {desc.description && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,.6)",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          overflow: "hidden",
                          direction: "rtl",
                          textAlign: "right",
                        }}
                      >
                        {desc.description}
                      </Typography>
                    )}
                  </Box>
                </ListItem>
              ))
            ) : (
              <ListItem sx={{ px: 0, py: 0.7 }}>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <CheckCircleIcon
                    sx={{
                      fontSize: 18,
                      color: "#4FC3F7",
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                    }}
                  />
                </ListItemIcon>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.6)" }}
                >
                  امکانات پایه
                </Typography>
              </ListItem>
            )}
          </List>

          {/* CTA Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{
              mt: "auto",
              py: 1.5,
              borderRadius: 3,
              fontWeight: 700,
              fontSize: "1rem",
              textTransform: "none",
              boxShadow: hasDiscount
                ? "0 8px 25px rgba(0, 72, 255, 0.3)"
                : "0 8px 25px rgba(79, 195, 247, 0.3)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: hasDiscount
                  ? "0 12px 35px rgba(68, 0, 255, 0.5)"
                  : "0 12px 35px rgba(79, 195, 247, 0.5)",
              },
            }}
          >
            {hasDiscount ? " شروع ویژه" : "شروع همکاری"}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
}
